import { useState, useEffect } from 'react';
import { FiEdit2, FiSave, FiX, FiCheck, FiAlertCircle } from 'react-icons/fi';
import { subscriptionsAPI } from '../../services/api';
import { useApp } from '../../context/AppContext';
import { SUCCESS_MESSAGES, SUBSCRIPTION_PLANS } from '../../utils/constants';

const PLAN_LABELS = {
  [SUBSCRIPTION_PLANS.BASIC]: 'Básico',
  [SUBSCRIPTION_PLANS.PROFESSIONAL]: 'Profesional',
  [SUBSCRIPTION_PLANS.ENTERPRISE]: 'Empresarial'
};

const SubscriptionPlansManagement = () => {
  const { addNotification } = useApp();
  const [plans, setPlans] = useState([]);
  const [planFeatures, setPlanFeatures] = useState({}); // Store features by plan ID
  const [loading, setLoading] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [editFormData, setEditFormData] = useState({});

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      setLoading(true);
      const data = await subscriptionsAPI.getPlans({ include_inactive: true });
      setPlans(data);

      // Load features for all plans
      const featuresPromises = data.map(async (plan) => {
        try {
          const planFeaturesData = await subscriptionsAPI.getPlanFeatures(plan.id);
          return { planId: plan.id, features: planFeaturesData.features };
        } catch (error) {
          console.error(`Error loading features for plan ${plan.id}:`, error);
          return {
            planId: plan.id,
            features: {
              incident_management: true,
              custom_cleaning_types: true,
              custom_bed_dispositions: true
            }
          };
        }
      });

      const allFeatures = await Promise.all(featuresPromises);
      const featuresMap = {};
      allFeatures.forEach(({ planId, features }) => {
        featuresMap[planId] = features;
      });
      setPlanFeatures(featuresMap);
    } catch (error) {
      showNotification('Error al cargar los planes', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (plan) => {
    setEditingPlan(plan.id);

    // Get plan features from state
    const features = planFeatures[plan.id] || {
      incident_management: true,
      custom_cleaning_types: true,
      custom_bed_dispositions: true
    };

    setEditFormData({
      name: plan.name,
      description: plan.description || '',
      max_rooms: plan.max_rooms || '',
      max_common_spaces: plan.max_common_spaces || '',
      max_users: plan.max_users || '',
      monthly_price: plan.monthly_price,
      monthly_price_original: plan.monthly_price_original || '',
      annual_price: plan.annual_price,
      annual_price_original: plan.annual_price_original || '',
      annual_discount_percentage: plan.annual_discount_percentage || '',
      prices_include_iva: plan.prices_include_iva || false,
      iva_percentage: plan.iva_percentage || 21,
      trial_days: plan.trial_days,
      display_order: plan.display_order,
      is_active: plan.is_active,
      // Features
      incident_management: features.incident_management,
      custom_cleaning_types: features.custom_cleaning_types,
      custom_bed_dispositions: features.custom_bed_dispositions
    });
  };

  const handleCancelEdit = () => {
    setEditingPlan(null);
    setEditFormData({});
  };

  const handleSaveEdit = async (planId) => {
    try {
      setLoading(true);

      // Preparar datos del plan
      const updateData = {
        name: editFormData.name,
        description: editFormData.description,
        max_rooms: editFormData.max_rooms === '' ? null : parseInt(editFormData.max_rooms),
        max_common_spaces: editFormData.max_common_spaces === '' ? null : parseInt(editFormData.max_common_spaces),
        max_users: editFormData.max_users === '' ? null : parseInt(editFormData.max_users),
        monthly_price: parseFloat(editFormData.monthly_price),
        monthly_price_original: editFormData.monthly_price_original === '' ? null : parseFloat(editFormData.monthly_price_original),
        annual_price: parseFloat(editFormData.annual_price),
        annual_price_original: editFormData.annual_price_original === '' ? null : parseFloat(editFormData.annual_price_original),
        annual_discount_percentage: editFormData.annual_discount_percentage === '' ? null : parseInt(editFormData.annual_discount_percentage),
        prices_include_iva: editFormData.prices_include_iva,
        iva_percentage: parseInt(editFormData.iva_percentage),
        trial_days: parseInt(editFormData.trial_days),
        display_order: parseInt(editFormData.display_order),
        is_active: editFormData.is_active
      };

      // Preparar datos de features
      const featuresData = {
        incident_management: editFormData.incident_management,
        custom_cleaning_types: editFormData.custom_cleaning_types,
        custom_bed_dispositions: editFormData.custom_bed_dispositions
      };

      // Actualizar plan y features en paralelo
      await Promise.all([
        subscriptionsAPI.updatePlan(planId, updateData),
        subscriptionsAPI.updatePlanFeatures(planId, featuresData)
      ]);

      showNotification('Plan actualizado correctamente', 'success');

      setEditingPlan(null);
      setEditFormData({});
      fetchPlans();
    } catch (error) {
      showNotification(
        error.response?.data?.detail || 'Error al actualizar el plan',
        'error'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (plan) => {
    try {
      setLoading(true);
      await subscriptionsAPI.updatePlan(plan.id, { is_active: !plan.is_active });
      showNotification(
        plan.is_active ? 'Plan desactivado' : 'Plan activado',
        'success'
      );
      fetchPlans();
    } catch (error) {
      showNotification(
        error.response?.data?.detail || 'Error al cambiar estado del plan',
        'error'
      );
    } finally {
      setLoading(false);
    }
  };

  const showNotification = (message, type) => {
    addNotification({ message, type });
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR'
    }).format(price);
  };

  const calculateMonthlyDiscount = (monthlyPrice, annualPrice) => {
    const monthlyTotal = monthlyPrice * 12;
    const savings = monthlyTotal - annualPrice;
    const discount = (savings / monthlyTotal) * 100;
    return Math.round(discount);
  };

  const calculatePriceWithIVA = (price, ivaPercentage = 21) => {
    return price * (1 + ivaPercentage / 100);
  };

  const calculatePriceWithoutIVA = (priceWithIVA, ivaPercentage = 21) => {
    return priceWithIVA / (1 + ivaPercentage / 100);
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-800">
              Gestión de Planes de Suscripción
            </h1>
            <p className="text-gray-600 mt-2">
              Configura los precios, límites y características de cada plan. Los cambios se reflejarán automáticamente en la landing page.
            </p>
          </div>
        </div>

        {/* Información importante */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-3">
            <FiAlertCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-blue-900">
              <p className="font-semibold mb-1">Importante:</p>
              <ul className="list-disc list-inside space-y-1">
                <li>Los cambios en los límites solo afectan a <strong>nuevas suscripciones</strong></li>
                <li>Las suscripciones existentes mantienen los límites del momento de creación</li>
                <li>Deja el campo de habitaciones, espacios comunes o usuarios vacío para indicar "ilimitado"</li>
                <li>Los cambios de precio se reflejan inmediatamente en la landing page</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Plans Table */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          {loading && plans.length === 0 ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">Cargando planes...</p>
            </div>
          ) : plans.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <FiAlertCircle className="mx-auto text-4xl mb-2" />
              <p>No hay planes configurados</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Plan
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Límites
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Precio Mensual
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Precio Anual
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      IVA
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Prueba
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Estado
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Funcionalidades
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {plans.map((plan) => (
                    <tr key={plan.id} className={!plan.is_active ? 'bg-gray-50 opacity-60' : ''}>
                      {/* Plan Name & Description */}
                      <td className="px-6 py-4">
                        {editingPlan === plan.id ? (
                          <div className="space-y-2">
                            <input
                              type="text"
                              value={editFormData.name}
                              onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                              className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              placeholder="Nombre del plan"
                            />
                            <textarea
                              value={editFormData.description}
                              onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                              className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              rows="2"
                              placeholder="Descripción"
                            />
                          </div>
                        ) : (
                          <div>
                            <div className="font-semibold text-gray-900">{plan.name}</div>
                            <div className="text-xs text-gray-600 uppercase font-medium mt-1">
                              {PLAN_LABELS[plan.plan]}
                            </div>
                            {plan.description && (
                              <div className="text-sm text-gray-600 mt-1">{plan.description}</div>
                            )}
                          </div>
                        )}
                      </td>

                      {/* Limits */}
                      <td className="px-6 py-4">
                        {editingPlan === plan.id ? (
                          <div className="space-y-2">
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">Habitaciones</label>
                              <input
                                type="number"
                                min="0"
                                value={editFormData.max_rooms}
                                onChange={(e) => setEditFormData({ ...editFormData, max_rooms: e.target.value })}
                                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                                placeholder="Ilimitado"
                              />
                            </div>
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">Espacios Comunes</label>
                              <input
                                type="number"
                                min="0"
                                value={editFormData.max_common_spaces}
                                onChange={(e) => setEditFormData({ ...editFormData, max_common_spaces: e.target.value })}
                                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                                placeholder="Ilimitado"
                              />
                            </div>
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">Usuarios</label>
                              <input
                                type="number"
                                min="0"
                                value={editFormData.max_users}
                                onChange={(e) => setEditFormData({ ...editFormData, max_users: e.target.value })}
                                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                                placeholder="Ilimitado"
                              />
                            </div>
                          </div>
                        ) : (
                          <div className="text-sm">
                            <div className="text-gray-900">
                              🏠 {plan.max_rooms ? `${plan.max_rooms} hab.` : 'Ilimitadas'}
                            </div>
                            <div className="text-gray-900 mt-1">
                              🗺️ {plan.max_common_spaces ? `${plan.max_common_spaces} esp. comunes` : 'Ilimitados'}
                            </div>
                            <div className="text-gray-900 mt-1">
                              👥 {plan.max_users ? `${plan.max_users} usuarios` : 'Ilimitados'}
                            </div>
                          </div>
                        )}
                      </td>

                      {/* Monthly Price */}
                      <td className="px-6 py-4">
                        {editingPlan === plan.id ? (
                          <div className="space-y-2">
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">Precio Actual €/mes</label>
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={editFormData.monthly_price}
                                onChange={(e) => setEditFormData({ ...editFormData, monthly_price: e.target.value })}
                                className="w-24 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                              />
                            </div>
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">Precio Original €/mes (opcional)</label>
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={editFormData.monthly_price_original}
                                onChange={(e) => setEditFormData({ ...editFormData, monthly_price_original: e.target.value })}
                                className="w-24 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                                placeholder="Sin oferta"
                              />
                            </div>
                            {editFormData.monthly_price_original && editFormData.monthly_price && (
                              <div className="text-xs text-red-600">
                                Descuento: {Math.round((1 - parseFloat(editFormData.monthly_price) / parseFloat(editFormData.monthly_price_original)) * 100)}%
                              </div>
                            )}
                          </div>
                        ) : (
                          <div>
                            <div className="font-semibold text-gray-900">
                              {formatPrice(plan.monthly_price)}/mes
                            </div>
                            {plan.monthly_price_original && (
                              <div className="text-xs text-gray-500 line-through">
                                {formatPrice(plan.monthly_price_original)}
                              </div>
                            )}
                          </div>
                        )}
                      </td>

                      {/* Annual Price */}
                      <td className="px-6 py-4">
                        {editingPlan === plan.id ? (
                          <div className="space-y-2">
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">Precio Actual €/año</label>
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={editFormData.annual_price}
                                onChange={(e) => setEditFormData({ ...editFormData, annual_price: e.target.value })}
                                className="w-24 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                              />
                            </div>
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">Precio Original €/año (opcional)</label>
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={editFormData.annual_price_original}
                                onChange={(e) => setEditFormData({ ...editFormData, annual_price_original: e.target.value })}
                                className="w-24 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                                placeholder="Sin oferta"
                              />
                            </div>
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">% Ahorro Anual (calculado auto)</label>
                              <input
                                type="number"
                                min="0"
                                max="100"
                                value={editFormData.annual_discount_percentage}
                                onChange={(e) => setEditFormData({ ...editFormData, annual_discount_percentage: e.target.value })}
                                className="w-24 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                                placeholder="Auto"
                              />
                            </div>
                            {editFormData.monthly_price && editFormData.annual_price && (
                              <div className="text-xs text-green-600">
                                Ahorro anual vs mensual: {calculateMonthlyDiscount(
                                  parseFloat(editFormData.monthly_price),
                                  parseFloat(editFormData.annual_price)
                                )}%
                              </div>
                            )}
                            {editFormData.annual_price_original && editFormData.annual_price && (
                              <div className="text-xs text-red-600">
                                Descuento en oferta: {Math.round((1 - parseFloat(editFormData.annual_price) / parseFloat(editFormData.annual_price_original)) * 100)}%
                              </div>
                            )}
                          </div>
                        ) : (
                          <div>
                            <div className="font-semibold text-gray-900">
                              {formatPrice(plan.annual_price)}/año
                            </div>
                            {plan.annual_price_original && (
                              <div className="text-xs text-gray-500 line-through">
                                {formatPrice(plan.annual_price_original)}
                              </div>
                            )}
                            <div className="text-xs text-green-600 mt-1">
                              Ahorro anual: {plan.annual_discount_percentage || calculateMonthlyDiscount(
                                parseFloat(plan.monthly_price),
                                parseFloat(plan.annual_price)
                              )}%
                            </div>
                            {plan.prices_include_iva && (
                              <div className="text-xs text-blue-600 mt-1">
                                IVA ({plan.iva_percentage}%) incluido
                              </div>
                            )}
                          </div>
                        )}
                      </td>

                      {/* IVA */}
                      <td className="px-6 py-4">
                        {editingPlan === plan.id ? (
                          <div className="space-y-2">
                            <div>
                              <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={editFormData.prices_include_iva}
                                  onChange={(e) => setEditFormData({ ...editFormData, prices_include_iva: e.target.checked })}
                                  className="rounded border-gray-300 text-blue-600 focus:ring-2 focus:ring-blue-500"
                                />
                                Precios con IVA
                              </label>
                            </div>
                            {editFormData.prices_include_iva && (
                              <div>
                                <label className="block text-xs text-gray-600 mb-1">% IVA</label>
                                <input
                                  type="number"
                                  min="0"
                                  max="100"
                                  value={editFormData.iva_percentage}
                                  onChange={(e) => setEditFormData({ ...editFormData, iva_percentage: e.target.value })}
                                  className="w-20 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                                />
                              </div>
                            )}
                            {editFormData.prices_include_iva && editFormData.monthly_price && (
                              <div className="text-xs space-y-1">
                                <div className="text-gray-600">
                                  Mensual sin IVA: {formatPrice(calculatePriceWithoutIVA(parseFloat(editFormData.monthly_price), parseInt(editFormData.iva_percentage)))}
                                </div>
                                <div className="text-gray-600">
                                  Anual sin IVA: {formatPrice(calculatePriceWithoutIVA(parseFloat(editFormData.annual_price), parseInt(editFormData.iva_percentage)))}
                                </div>
                              </div>
                            )}
                            {!editFormData.prices_include_iva && editFormData.monthly_price && (
                              <div className="text-xs space-y-1">
                                <div className="text-gray-600">
                                  Mensual con IVA: {formatPrice(calculatePriceWithIVA(parseFloat(editFormData.monthly_price), parseInt(editFormData.iva_percentage || 21)))}
                                </div>
                                <div className="text-gray-600">
                                  Anual con IVA: {formatPrice(calculatePriceWithIVA(parseFloat(editFormData.annual_price), parseInt(editFormData.iva_percentage || 21)))}
                                </div>
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="text-sm">
                            {plan.prices_include_iva ? (
                              <div>
                                <div className="text-gray-900 font-semibold">
                                  ✓ Incluido ({plan.iva_percentage}%)
                                </div>
                                <div className="text-xs text-gray-600 mt-1">
                                  Sin IVA: {formatPrice(calculatePriceWithoutIVA(parseFloat(plan.monthly_price), plan.iva_percentage))}/mes
                                </div>
                              </div>
                            ) : (
                              <div>
                                <div className="text-gray-900">
                                  ✗ No incluido
                                </div>
                                <div className="text-xs text-gray-600 mt-1">
                                  Con IVA: {formatPrice(calculatePriceWithIVA(parseFloat(plan.monthly_price), 21))}/mes
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </td>

                      {/* Trial Days */}
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {editingPlan === plan.id ? (
                          <div>
                            <label className="block text-xs text-gray-600 mb-1">Días</label>
                            <input
                              type="number"
                              min="0"
                              value={editFormData.trial_days}
                              onChange={(e) => setEditFormData({ ...editFormData, trial_days: e.target.value })}
                              className="w-20 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                            />
                          </div>
                        ) : (
                          `${plan.trial_days} días`
                        )}
                      </td>

                      {/* Status */}
                      <td className="px-6 py-4">
                        <button
                          onClick={() => handleToggleActive(plan)}
                          disabled={loading}
                          title={plan.is_active ? 'Hacer clic para desactivar' : 'Hacer clic para activar'}
                          className={`flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold transition-all cursor-pointer ${
                            plan.is_active
                              ? 'bg-green-100 text-green-800 hover:bg-green-200 hover:shadow-md'
                              : 'bg-red-100 text-red-800 hover:bg-red-200 hover:shadow-md'
                          } disabled:opacity-50 disabled:cursor-not-allowed`}
                        >
                          {plan.is_active ? (
                            <>
                              <FiCheck className="w-3 h-3" />
                              Activo
                            </>
                          ) : (
                            <>
                              <FiX className="w-3 h-3" />
                              Inactivo
                            </>
                          )}
                        </button>
                        <p className="text-xs text-gray-500 mt-1">
                          {plan.is_active ? 'Clic para desactivar' : 'Clic para activar'}
                        </p>
                      </td>

                      {/* Features */}
                      <td className="px-6 py-4">
                        {editingPlan === plan.id ? (
                          <div className="space-y-2">
                            <label className="flex items-center gap-2 text-xs text-gray-700 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={editFormData.incident_management}
                                onChange={(e) => setEditFormData({ ...editFormData, incident_management: e.target.checked })}
                                className="rounded border-gray-300 text-blue-600 focus:ring-2 focus:ring-blue-500"
                              />
                              Gestión de incidencias
                            </label>
                            <label className="flex items-center gap-2 text-xs text-gray-700 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={editFormData.custom_cleaning_types}
                                onChange={(e) => setEditFormData({ ...editFormData, custom_cleaning_types: e.target.checked })}
                                className="rounded border-gray-300 text-blue-600 focus:ring-2 focus:ring-blue-500"
                              />
                              Tipos de limpieza personalizados
                            </label>
                            <label className="flex items-center gap-2 text-xs text-gray-700 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={editFormData.custom_bed_dispositions}
                                onChange={(e) => setEditFormData({ ...editFormData, custom_bed_dispositions: e.target.checked })}
                                className="rounded border-gray-300 text-blue-600 focus:ring-2 focus:ring-blue-500"
                              />
                              Disposiciones de camas personalizadas
                            </label>
                          </div>
                        ) : (
                          <div className="text-xs space-y-1">
                            <div className={`flex items-center gap-1 ${planFeatures[plan.id]?.incident_management ? 'text-green-700' : 'text-red-700'}`}>
                              {planFeatures[plan.id]?.incident_management ? '✓' : '✗'} Incidencias
                            </div>
                            <div className={`flex items-center gap-1 ${planFeatures[plan.id]?.custom_cleaning_types ? 'text-green-700' : 'text-red-700'}`}>
                              {planFeatures[plan.id]?.custom_cleaning_types ? '✓' : '✗'} Tipos limpieza
                            </div>
                            <div className={`flex items-center gap-1 ${planFeatures[plan.id]?.custom_bed_dispositions ? 'text-green-700' : 'text-red-700'}`}>
                              {planFeatures[plan.id]?.custom_bed_dispositions ? '✓' : '✗'} Disposiciones
                            </div>
                          </div>
                        )}
                      </td>

                      {/* Actions */}
                      <td className="px-6 py-4">
                        {editingPlan === plan.id ? (
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleSaveEdit(plan.id)}
                              disabled={loading}
                              className="flex items-center gap-1 px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 text-sm"
                            >
                              <FiSave className="w-4 h-4" />
                              Guardar
                            </button>
                            <button
                              onClick={handleCancelEdit}
                              disabled={loading}
                              className="flex items-center gap-1 px-3 py-1 bg-gray-300 text-gray-700 rounded hover:bg-gray-400 disabled:opacity-50 text-sm"
                            >
                              <FiX className="w-4 h-4" />
                              Cancelar
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => handleEditClick(plan)}
                            disabled={loading}
                            className="flex items-center gap-1 px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 text-sm"
                          >
                            <FiEdit2 className="w-4 h-4" />
                            Editar
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Info section */}
        <div className="mt-6 bg-white rounded-lg shadow-md p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">
            Vista Previa en la Landing Page
          </h2>
          <p className="text-gray-600 mb-4">
            Los cambios que realices aquí se reflejarán automáticamente cuando los usuarios visiten la landing page. Puedes previsualizar cómo se verán los planes visitando la página principal.
          </p>
          <div className="grid md:grid-cols-3 gap-4 mt-4">
            {plans.filter(p => p.is_active).map((plan) => (
              <div
                key={plan.id}
                className="border border-gray-200 rounded-lg p-4"
              >
                <h3 className="font-semibold text-gray-900 mb-1">{plan.name}</h3>
                <p className="text-xs text-gray-600 mb-3">{plan.description}</p>
                <div className="text-2xl font-bold text-blue-600 mb-2">
                  {formatPrice(plan.monthly_price)}
                  <span className="text-sm text-gray-600">/mes</span>
                </div>
                <div className="text-sm text-gray-600 mb-3">
                  {formatPrice(plan.annual_price)}/año
                  <span className="text-xs text-green-600 ml-1">
                    (Ahorra {calculateMonthlyDiscount(
                      parseFloat(plan.monthly_price),
                      parseFloat(plan.annual_price)
                    )}%)
                  </span>
                </div>
                <div className="text-sm text-gray-600 space-y-1">
                  <div>✓ Hasta {plan.max_rooms || '∞'} habitaciones</div>
                  <div>✓ Hasta {plan.max_common_spaces || '∞'} espacios comunes</div>
                  <div>✓ {plan.max_users ? `Hasta ${plan.max_users}` : 'Usuarios ilimitados'}</div>
                  <div>✓ Prueba de {plan.trial_days} días</div>
                  <div className="border-t border-gray-200 my-2 pt-2">
                    <div className="font-semibold text-gray-700 mb-1">Funcionalidades:</div>
                    <div className={planFeatures[plan.id]?.incident_management ? 'text-green-700' : 'text-red-700'}>
                      {planFeatures[plan.id]?.incident_management ? '✓' : '✗'} Gestión de incidencias
                    </div>
                    <div className={planFeatures[plan.id]?.custom_cleaning_types ? 'text-green-700' : 'text-red-700'}>
                      {planFeatures[plan.id]?.custom_cleaning_types ? '✓' : '✗'} Tipos de limpieza personalizados
                    </div>
                    <div className={planFeatures[plan.id]?.custom_bed_dispositions ? 'text-green-700' : 'text-red-700'}>
                      {planFeatures[plan.id]?.custom_bed_dispositions ? '✓' : '✗'} Disposiciones de camas personalizadas
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionPlansManagement;
